const crypto = require('crypto');



const secretKey = process.env.VUE_APP_CRYPTO_SECRET_KEY;

const algorithm = process.env.VUE_APP_CRYPTO_ALGORITHM || 'aes-256-gcm';

const hashAlgorithm = process.env.VUE_APP_CRYPTO_HASH_ALGORITHM || 'sha256';

const outputEncoding = process.env.VUE_APP_CRYPTO_OUTPUT_ENCODING || 'hex';

const inputEncoding = process.env.VUE_APP_CRYPTO_INPUT_ENCODING || 'utf8';



if (!secretKey) {

    console.error("VUE_APP_CRYPTO_SECRET_KEY is not defined in .env");

}



export function encryptSearchCode(textToEncrypt) {

    try {

        // 1. Derive key using configured hash algorithm

        const key = crypto.createHash(hashAlgorithm).update(String(secretKey)).digest();

        const iv = crypto.randomBytes(12);



        // 2. Encrypt using configured algorithm

        const cipher = crypto.createCipheriv(algorithm, key, iv);

        // Set AuthTag length to 16 bytes (128 bits) explicitly

        cipher.setAAD(Buffer.alloc(0)); // Set empty Additional Authenticated Data to ensure proper tag generation

        let encrypted = cipher.update(textToEncrypt, inputEncoding, outputEncoding);

        encrypted += cipher.final(outputEncoding);

        const authTag = cipher.getAuthTag();

        

        // Ensure AuthTag is exactly 16 bytes for AES-256-GCM

        if (authTag.length !== 16) {

            throw new Error(`Invalid AuthTag length: ${authTag.length} bytes, expected 16 bytes`);

        }



        // 3. Format: IV:Content:Tag using configured encoding

        return `${iv.toString(outputEncoding)}:${encrypted}:${authTag.toString(outputEncoding)}`;

    } catch (error) {

        console.error('Encryption error:', error);

        return null;

    }

}



export function decryptSearchCode(textToDecrypt) {

    try {

        const parts = textToDecrypt.split(':');

        if (parts.length !== 3) return null;



        const iv = Buffer.from(parts[0], outputEncoding);

        const encryptedText = parts[1];

        const authTag = Buffer.from(parts[2], outputEncoding);



        // Derive key using configured hash algorithm - MUST match encryption

        const key = crypto.createHash(hashAlgorithm).update(String(secretKey)).digest();



        const decipher = crypto.createDecipheriv(algorithm, key, iv);

        decipher.setAAD(Buffer.alloc(0)); // Match the AAD used during encryption

        decipher.setAuthTag(authTag);



        let decrypted = decipher.update(encryptedText, outputEncoding, inputEncoding);

        decrypted += decipher.final(inputEncoding);



        return decrypted;

    } catch (error) {

        console.error('Decryption error:', error);

        return null;

    }

}


export function decryptSearchCodeWithExpiry(textToDecrypt) {
    try {
        const parts = textToDecrypt.split(':');
        // Verificar si es URL con expiración (4 partes) o normal (3 partes)
        if (parts.length === 4 && parts[3] === 'EXP') {
            // URL con expiración
            const iv = Buffer.from(parts[0], outputEncoding);
            const encryptedText = parts[1];
            const authTag = Buffer.from(parts[2], outputEncoding);

            const key = crypto.createHash(hashAlgorithm).update(String(secretKey)).digest();
            const decipher = crypto.createDecipheriv(algorithm, key, iv);
            decipher.setAAD(Buffer.alloc(0));
            decipher.setAuthTag(authTag);

            let decrypted = decipher.update(encryptedText, outputEncoding, inputEncoding);
            decrypted += decipher.final(inputEncoding);

            // Parse del payload
            const payload = JSON.parse(decrypted);
            
            // Verificar expiración
            if (Date.now() > payload.exp) {
                console.warn('URL expired');
                return { expired: true, code: null };
            }

            return { expired: false, code: payload.code };
        } else if (parts.length === 3) {
            // URL sin expiración (backward compatibility)
            const result = decryptSearchCode(textToDecrypt);
            return result ? { expired: false, code: result } : null;
        }

        return null;
    } catch (error) {
        console.error('Decryption with expiry error:', error);
        return null;
    }
}

// Encriptar token usando AES-256-GCM (misma función que order pero con prefijo diferente)
export function encryptToken(token) {
    try {
        const secretKey = new TextEncoder().encode(process.env.VUE_APP_CRYPTO_SECRET_KEY.padEnd(32, '0'));
        const iv = crypto.getRandomValues(new Uint8Array(12));
        
        const encoder = new TextEncoder();
        const data = encoder.encode(token);
        
        return crypto.subtle.importKey(
            'raw',
            secretKey,
            { name: 'AES-GCM' },
            false,
            ['encrypt']
        ).then(key => {
            return crypto.subtle.encrypt(
                { name: 'AES-GCM', iv: iv },
                key,
                data
            );
        }).then(encrypted => {
            const encryptedArray = new Uint8Array(encrypted);
            const ivHex = Array.from(iv).map(b => b.toString(16).padStart(2, '0')).join('');
            const encryptedHex = Array.from(encryptedArray).map(b => b.toString(16).padStart(2, '0')).join('');
            
            // Formato: TOKEN:iv:encrypted_data
            return `TOKEN:${ivHex}:${encryptedHex}`;
        });
    } catch (error) {
        console.error('Token encryption error:', error);
        return null;
    }
}

// Desencriptar token 
export function decryptToken(encryptedToken) {
    try {
        // Verificar formato TOKEN:iv:encrypted_data
        if (!encryptedToken || !encryptedToken.startsWith('TOKEN:')) {
            return null; // No es un token encriptado
        }
        
        const parts = encryptedToken.split(':');
        if (parts.length !== 3) {
            return null;
        }
        
        const secretKey = new TextEncoder().encode(process.env.VUE_APP_CRYPTO_SECRET_KEY.padEnd(32, '0'));
        const iv = new Uint8Array(parts[1].match(/.{2}/g).map(byte => parseInt(byte, 16)));
        const encryptedData = new Uint8Array(parts[2].match(/.{2}/g).map(byte => parseInt(byte, 16)));
        
        return crypto.subtle.importKey(
            'raw',
            secretKey,
            { name: 'AES-GCM' },
            false,
            ['decrypt']
        ).then(key => {
            return crypto.subtle.decrypt(
                { name: 'AES-GCM', iv: iv },
                key,
                encryptedData
            );
        }).then(decrypted => {
            const decoder = new TextDecoder();
            return decoder.decode(decrypted);
        }).catch(error => {
            console.error('Token decryption failed:', error);
            return null;
        });
    } catch (error) {
        console.error('Token decryption error:', error);
        return null;
    }
}

// Generar URL con formato order+token (order encriptado, token en texto plano)
export function generateOrderTokenUrl(orderCode, plainToken, baseUrl = 'https://seguimiento.smartquick.com.co') {
    try {
        const encryptedOrder = encryptSearchCode(orderCode);
        if (!encryptedOrder) {
            throw new Error('Failed to encrypt order code');
        }

        const url = new URL(baseUrl);
        url.searchParams.set('order', encryptedOrder);
        url.searchParams.set('token', plainToken); // Token en texto plano
        
        return url.toString();
    } catch (error) {
        console.error('Generate order+token URL error:', error);
        return null;
    }
}
